import { ReactNode, useState } from "react"
import { DateTime } from "luxon"
import { Box, Collapse, IconButton, LinearProgress, Stack, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faChevronDown, faChevronUp, faPenToSquare } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useDateTime } from "../../../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { MatchEnvironment } from "../../../../../../environment/components/MatchEnvironment"
import { EnvironmentType } from "../../../../../../environment/envTypes"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardLoadingButton } from "../../../../../../../shared/components/buttons/LoadingButton"
import { MeetingServiceOrder } from "../../meetingServiceTypes"
import { ProductionOrderCardMetaRow } from "./ProductionOrderCardMetaRow"
import { ProductionOrderLineRow } from "./ProductionOrderLineRow"
import {
    ProductionDeliveryGroup,
    ProductionEditLine,
    ProductionProductOption,
    SetLineProductInput,
} from "../useProductionOrderEdit"

const useStyles = makeStyles()((theme) => ({
    list: {
        display: "flex",
        flexDirection: "column",
    },
    section: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
    },
    deliveryHeaderWrapper: {
        position: "relative",
    },
    // Background-refresh indicator on the delivery being saved (an order can have several deliveries).
    savingBar: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1,
    },
    deliveryHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            flexWrap: "wrap",
        },
    },
    headerLeft: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1rem",
        minWidth: 0,
    },
    chevron: {
        fontSize: "1.25rem",
        color: theme.palette.tailwind.slate[500],
    },
    chevronButton: {
        color: theme.palette.tailwind.slate[500],
    },
    chevronStatic: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: ".3125rem",
        color: theme.palette.tailwind.slate[500],
    },
    timeColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
    },
    bigTime: {
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: 1.6,
        letterSpacing: ".009375rem",
        color: theme.palette.tailwind.slate[950],
        whiteSpace: "nowrap",
    },
    shortTime: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.5,
        letterSpacing: ".009375rem",
        color: theme.palette.tailwind.zinc[500],
        whiteSpace: "nowrap",
    },
    bar: {
        flexShrink: 0,
        width: ".125rem",
        height: "3.5rem",
        background: theme.palette.tailwind.blue[200],
    },
    editButton: {
        flexShrink: 0,
        width: "3rem",
        height: "3rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[700],
        "& svg": {
            fontSize: "1.125rem",
        },
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: ".75rem",
        [theme.breakpoints.down("md")]: {
            marginLeft: "auto",
        },
    },
    editActionButton: {
        flexShrink: 0,
        "&.MuiButton-root": {
            height: "3rem",
            borderRadius: ".75rem",
            fontSize: ".875rem",
            fontWeight: 500,
            letterSpacing: ".02875rem",
        },
    },
    saveButton: {
        "&.MuiButton-root": {
            padding: ".75rem 1.25rem .75rem 1rem",
            backgroundColor: theme.palette.tailwind.slate[800],
            color: theme.palette.common.white,
        },
        "&.MuiButton-root:hover": {
            backgroundColor: theme.palette.tailwind.slate[700],
        },
        // Show a real disabled state (the dark background above otherwise hides it).
        "&.MuiButton-root.Mui-disabled": {
            backgroundColor: theme.palette.tailwind.slate[200],
            color: theme.palette.tailwind.slate[400],
        },
    },
    cancelButton: {
        "&.MuiButton-root": {
            padding: ".75rem 1.25rem",
        },
    },
    itemsArea: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        padding: "1rem 1rem 1rem 8rem",
        background: theme.palette.tailwind.slate[50],
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        [theme.breakpoints.down("md")]: {
            paddingLeft: "1rem",
        },
    },
    itemsAreaLast: {
        borderBottom: "none",
    },
    editItems: {
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        padding: "1rem 1rem 0 7.5rem",
        background: theme.palette.tailwind.slate[50],
        [theme.breakpoints.down("md")]: {
            paddingLeft: "1rem",
        },
    },
}))

type MergedDeliveryGroup = Readonly<{
    deliveryKey: string
    deliveryTime: DateTime
    lines: ProductionEditLine[]
    sourceKeys: string[]
}>

function mergeGroupsByTime(groups: ReadonlyArray<ProductionDeliveryGroup>): MergedDeliveryGroup[] {
    const merged = new Map<
        string,
        { deliveryKey: string; deliveryTime: DateTime; lines: ProductionEditLine[]; sourceKeys: string[] }
    >()

    for (const group of groups) {
        const timeKey = group.deliveryTime.isValid ? (group.deliveryTime.toISO() ?? "invalid") : "invalid"
        const existing = merged.get(timeKey)
        if (existing) {
            existing.lines.push(...group.lines)
            existing.sourceKeys.push(group.deliveryKey)
        } else {
            merged.set(timeKey, {
                deliveryKey: timeKey,
                deliveryTime: group.deliveryTime,
                lines: [...group.lines],
                sourceKeys: [group.deliveryKey],
            })
        }
    }

    return Array.from(merged.values())
}

type Props = Readonly<{
    order: MeetingServiceOrder
    groups: ReadonlyArray<ProductionDeliveryGroup>
    meetingStart?: string
    meetingEnd?: string
    editingKey: string | null
    isSaving: boolean
    hasChanges: boolean
    hasIncompleteLine: boolean
    saveAttempted: boolean
    touchedKeys: ReadonlyArray<string>
    usedProductIds: ReadonlyArray<number>
    onCountChange: (key: string, value: number) => void
    onDeliveryTimeChange: (deliveryKey: string, value: DateTime) => void
    onRemove: (key: string) => void
    onStartEdit: (deliveryKey: string, deliveryTime: DateTime) => void
    onSave: () => void
    onCancel: () => void
    productOptions: ReadonlyArray<ProductionProductOption>
    isProductsLoading: boolean
    isEditLoading: boolean
    onProductChange: (key: string, product: SetLineProductInput) => void
    editFooter: ReactNode
}>

export function ProductionOrderLines({
    order,
    groups,
    meetingStart,
    meetingEnd,
    editingKey,
    isSaving,
    hasChanges,
    hasIncompleteLine,
    saveAttempted,
    touchedKeys,
    usedProductIds,
    onCountChange,
    onDeliveryTimeChange,
    onRemove,
    onStartEdit,
    onSave,
    onCancel,
    productOptions,
    isProductsLoading,
    isEditLoading,
    onProductChange,
    editFooter,
}: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set())

    const toggleGroup = (deliveryKey: string) =>
        setCollapsedKeys((prev) => {
            const next = new Set(prev)
            if (next.has(deliveryKey)) {
                next.delete(deliveryKey)
            } else {
                next.add(deliveryKey)
            }
            return next
        })

    const meetingStartDateTime = meetingStart ? dateTimeFactory.fromApi(meetingStart) : undefined
    const meetingEndDateTime = meetingEnd ? dateTimeFactory.fromApi(meetingEnd) : undefined
    const meetingStartLabel = meetingStartDateTime?.isValid
        ? dateTimeFactory.toHourMinute(meetingStartDateTime)
        : undefined
    const meetingEndLabel = meetingEndDateTime?.isValid ? dateTimeFactory.toHourMinute(meetingEndDateTime) : undefined

    const displayGroups = mergeGroupsByTime(groups)

    return (
        <Stack className={classes.list}>
            {displayGroups.map((group, index) => {
                const isValidTime = group.deliveryTime.isValid
                const time = isValidTime ? dateTimeFactory.toHourMinute(group.deliveryTime) : "-"
                const bigTimeLabel = meetingStartLabel ?? time
                const groupEditing = editingKey === group.deliveryKey
                const isGroupLoading = groupEditing && (isProductsLoading || isSaving)

                if (groupEditing) {
                    return (
                        <Box key={group.deliveryKey} className={classes.section}>
                            <Box className={classes.deliveryHeaderWrapper}>
                                <Box className={classes.deliveryHeader}>
                                    <Box className={classes.headerLeft}>
                                        <Box component="span" className={classes.chevronStatic}>
                                            <FontAwesomeIcon icon={faChevronUp} className={classes.chevron} />
                                        </Box>
                                        <Box className={classes.timeColumn}>
                                            <Typography variant="h6" component="span" className={classes.bigTime}>
                                                {bigTimeLabel}
                                            </Typography>
                                            {meetingEndLabel && (
                                                <Typography
                                                    variant="body1"
                                                    component="span"
                                                    className={classes.shortTime}
                                                >
                                                    {meetingEndLabel}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box className={classes.bar} />
                                        <ProductionOrderCardMetaRow order={order} />
                                    </Box>
                                    <Box className={classes.headerActions}>
                                        <LocalizedStrict id="meeting-service-production-view-cancel-button">
                                            <StandardButton
                                                fullWidth={false}
                                                type="button"
                                                size="small"
                                                variant="outlined"
                                                className={cx(classes.editActionButton, classes.cancelButton)}
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </StandardButton>
                                        </LocalizedStrict>
                                        <LocalizedStrict id="meeting-service-production-view-save-changes-button">
                                            <StandardLoadingButton
                                                fullWidth={false}
                                                size="small"
                                                variant="contained"
                                                className={cx(classes.editActionButton, classes.saveButton)}
                                                startIcon={<FontAwesomeIcon icon={faCheck} />}
                                                loading={isSaving}
                                                disabled={saveAttempted && hasIncompleteLine}
                                                onClick={onSave}
                                            >
                                                Update order
                                            </StandardLoadingButton>
                                        </LocalizedStrict>
                                    </Box>
                                </Box>
                                {isGroupLoading && <LinearProgress className={classes.savingBar} />}
                            </Box>
                            <Box className={classes.editItems}>
                                {group.lines.map((line) => (
                                    <ProductionOrderLineRow
                                        key={line.key}
                                        line={line}
                                        isEditing={groupEditing}
                                        productOptions={productOptions}
                                        isProductsLoading={isProductsLoading}
                                        isEditLoading={isEditLoading}
                                        isTouched={touchedKeys.includes(line.key)}
                                        usedProductIds={usedProductIds}
                                        onCountChange={onCountChange}
                                        onProductChange={onProductChange}
                                        onRemove={onRemove}
                                    />
                                ))}
                            </Box>
                            {editFooter}
                        </Box>
                    )
                }

                const isOpen = !collapsedKeys.has(group.deliveryKey)
                const isLastGroup = index === displayGroups.length - 1

                return (
                    <Box key={group.deliveryKey} className={classes.section}>
                        <Box className={classes.deliveryHeaderWrapper}>
                            <Box className={classes.deliveryHeader}>
                                <Box className={classes.headerLeft}>
                                    <IconButton
                                        size="small"
                                        className={classes.chevronButton}
                                        aria-label={l10n.getString(
                                            isOpen ? "user-home-meetings-collapse" : "user-home-meetings-expand",
                                        )}
                                        onClick={() => toggleGroup(group.deliveryKey)}
                                    >
                                        <FontAwesomeIcon
                                            icon={isOpen ? faChevronUp : faChevronDown}
                                            className={classes.chevron}
                                        />
                                    </IconButton>
                                    <Box className={classes.timeColumn}>
                                        <Typography variant="h6" component="span" className={classes.bigTime}>
                                            {bigTimeLabel}
                                        </Typography>
                                        {meetingEndLabel && (
                                            <Typography variant="body1" component="span" className={classes.shortTime}>
                                                {meetingEndLabel}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box className={classes.bar} />
                                    <ProductionOrderCardMetaRow order={order} />
                                </Box>
                                {/* Quick (inline) edit — gated to non-prod while the edit API is being tested. */}
                                <MatchEnvironment
                                    environments={[
                                        EnvironmentType.Local,
                                        EnvironmentType.Test,
                                        EnvironmentType.Demo,
                                        EnvironmentType.QA,
                                    ]}
                                >
                                    <LocalizedStrict
                                        id="meeting-service-production-view-edit-items-tooltip"
                                        attrs={{ title: true }}
                                    >
                                        <Tooltip arrow title="Edit">
                                            <IconButton
                                                size="small"
                                                className={classes.editButton}
                                                aria-label={l10n.getString(
                                                    "meeting-service-production-view-edit-items-button",
                                                )}
                                                onClick={() => onStartEdit(group.deliveryKey, group.deliveryTime)}
                                            >
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </IconButton>
                                        </Tooltip>
                                    </LocalizedStrict>
                                </MatchEnvironment>
                            </Box>
                            {isGroupLoading && <LinearProgress className={classes.savingBar} />}
                        </Box>

                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box className={cx(classes.itemsArea, isLastGroup && classes.itemsAreaLast)}>
                                {group.lines.map((line) => (
                                    <ProductionOrderLineRow
                                        key={line.key}
                                        line={line}
                                        isEditing={groupEditing}
                                        productOptions={productOptions}
                                        isProductsLoading={isProductsLoading}
                                        isEditLoading={isEditLoading}
                                        isTouched={touchedKeys.includes(line.key)}
                                        usedProductIds={usedProductIds}
                                        onCountChange={onCountChange}
                                        onProductChange={onProductChange}
                                        onRemove={onRemove}
                                    />
                                ))}
                            </Box>
                        </Collapse>
                    </Box>
                )
            })}
        </Stack>
    )
}
